import {useRefObjectAsForwardedRef} from '@primer/react'
import type React from 'react'
import {createContext, forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useRef, useState} from 'react'
import {useSuggestions} from './SuggestionsContext'
import {SubmitEvent} from '../types'
import {checkFilterQuerySync} from '../utils'
import {useFilterQuery} from './FilterQueryContext'
import {useFilter} from '.'

interface InputContext {
  caretRef: React.RefObject<HTMLSpanElement>
  inputFocused: boolean
  inputKeyDown: React.KeyboardEventHandler<Element>
  inputOnChange: React.ChangeEventHandler<Element>
  inputOnFocus: React.FocusEventHandler<Element>
  inputOnBlur: React.FocusEventHandler<Element>
  inputRef: React.RefObject<HTMLInputElement>
  inputSelectionEnd: number
  inputSelectionStart: number
  showSpinner: boolean
  updateStyledInputBlockCount: (count: number) => void
  suspendFocus: (element: HTMLElement) => void
  updateInputSelection: (selectionStart: number, selectionEnd?: number) => void
}

export const InputContext = createContext<InputContext | undefined>(undefined)

export const useInput = () => {
  const context = useContext(InputContext)
  if (!context) {
    throw new Error('useInput must be used inside a InputContext')
  }

  return context
}

interface InputContextProviderProps {
  caretRef: React.RefObject<HTMLSpanElement> | null
  children: React.ReactNode
  inputRef: React.RefObject<HTMLInputElement> | null
  showSpinner: boolean
}

export type InputContextRef = {
  updateCaretPosition: (start: number, end?: number) => void
  caretStart: number
  caretEnd: number
  inputHasFocus: boolean
  styledInputBlockCount: number
}

export const InputContextProvider = forwardRef<InputContextRef, InputContextProviderProps>(
  ({caretRef: forwardedCaretRef = null, children, inputRef: forwardedInputRef = null, showSpinner}, ref) => {
    const caretRef = useRef<HTMLSpanElement>(null)
    useRefObjectAsForwardedRef(forwardedCaretRef, caretRef)
    const inputRef = useRef<HTMLInputElement>(null)
    useRefObjectAsForwardedRef(forwardedInputRef, inputRef)
    const [isInteractingWithSuggestions, setIsInteractingWithSuggestions] = useState(false)
    const [isInputFocused, setIsInputFocused] = useState(false)
    const [inputSelectionStart, setInputSelectionStart] = useState(-1)
    const [inputSelectionEnd, setInputSelectionEnd] = useState(-1)

    const styledInputBlockCount = useRef<number>(0)

    const {
      hideSuggestions,
      suggestions,
      suggestionsContainerRef,
      activeSuggestionRef,
      activeSuggestion,
      setActiveSuggestion,
      suggestionListCurrentTop,
      setSuggestionListCurrentTop,
      suggestionsVisible,
      suggestionSelected,
      updateSuggestions,
    } = useSuggestions()
    const {forceReparse, onSubmit, rawFilterRef, updateFilter} = useFilterQuery()
    const {config} = useFilter()

    const updateInputSelection = useCallback((selectionStart: number, selectionEnd?: number | null) => {
      setInputSelectionStart(selectionStart)
      setInputSelectionEnd(selectionEnd ?? selectionStart)
    }, [])

    useImperativeHandle(ref, () => ({
      caretStart: inputSelectionStart,
      caretEnd: inputSelectionEnd,
      inputHasFocus: isInputFocused,
      styledInputBlockCount: styledInputBlockCount.current,
      updateCaretPosition: updateInputSelection,
    }))

    const inputKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        const container = suggestionsContainerRef?.current
        const offsetHeight = activeSuggestionRef?.current?.offsetHeight || 0
        if (!container) return

        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          hideSuggestions()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        } else if (event.key === 'ArrowDown') {
          if (activeSuggestion + 1 >= suggestions.length) {
            setActiveSuggestion(-1)
            setSuggestionListCurrentTop(0)
            container.scrollTop = 0
          } else {
            setActiveSuggestion(activeSuggestion + 1)
            setSuggestionListCurrentTop(suggestionListCurrentTop + offsetHeight)
            if (suggestionListCurrentTop > container.scrollHeight - container.offsetHeight)
              container.scrollTop += offsetHeight
          }
          event.preventDefault()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        } else if (event.key === 'ArrowUp') {
          // If the current suggestion is the top of the list, then move to the end
          if (activeSuggestion <= 0) {
            setActiveSuggestion(suggestions.length - 1)
            setSuggestionListCurrentTop(container.scrollHeight)
            container.scrollTop = container.scrollHeight
          } else {
            setActiveSuggestion(activeSuggestion - 1)
            setSuggestionListCurrentTop(suggestionListCurrentTop - offsetHeight)
            if (suggestionListCurrentTop - container.offsetHeight <= 0) container.scrollTop -= offsetHeight
          }
          event.preventDefault()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        } else if (event.key === 'Enter') {
          if (suggestionsVisible && activeSuggestion !== null) {
            const suggestion = suggestions[activeSuggestion]
            if (suggestion) {
              event.preventDefault()
              suggestionSelected(suggestion)
              inputRef.current?.focus()
            } else {
              onSubmit(SubmitEvent.ExplicitSubmit)
            }
          } else {
            onSubmit(SubmitEvent.ExplicitSubmit)
          }
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        } else if (event.key === 'Escape') {
          hideSuggestions()
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        } else if (event.key === 'Home' || event.key === 'End') {
          setActiveSuggestion(-1)
        }
      },
      [
        activeSuggestion,
        activeSuggestionRef,
        hideSuggestions,
        onSubmit,
        setActiveSuggestion,
        setSuggestionListCurrentTop,
        suggestionListCurrentTop,
        suggestionSelected,
        suggestions,
        suggestionsContainerRef,
        suggestionsVisible,
      ],
    )

    const inputOnFocus = useCallback(() => {
      setIsInputFocused(true)
      if (isInteractingWithSuggestions) {
        setIsInteractingWithSuggestions(false)
      } else {
        forceReparse(-1, query => {
          if (checkFilterQuerySync(query, rawFilterRef?.current)) updateSuggestions(query, inputSelectionStart)
        })
      }
    }, [isInteractingWithSuggestions, forceReparse, rawFilterRef, updateSuggestions, inputSelectionStart])

    const inputOnBlur = useCallback(() => {
      if (isInteractingWithSuggestions) {
        return
      }
      setIsInputFocused(false)
      forceReparse(-1)
      hideSuggestions()
    }, [isInteractingWithSuggestions, forceReparse, hideSuggestions])

    const inputOnChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>(
      e => {
        e.preventDefault()
        const caret = config.variant !== 'button' ? inputRef.current?.selectionStart ?? -1 : -1
        updateFilter(e.currentTarget.value, caret, query => {
          updateSuggestions(query, caret)
        })

        if (inputSelectionStart === e.currentTarget.selectionStart) {
          // Have to forcibly set the input selection to nothing and then reset it. This way, when the new input value is
          // set, the cursor will still have "changed", even if the cursor is in the same position
          updateInputSelection(-1)
          setTimeout(() => updateInputSelection(caret), 1)
        } else {
          updateInputSelection(caret)
        }
      },
      [config.variant, inputSelectionStart, updateFilter, updateInputSelection, updateSuggestions],
    )

    const updateStyledInputBlockCount = useCallback((count: number) => {
      styledInputBlockCount.current = count
    }, [])

    const suspendFocus = useCallback(() => {
      setIsInteractingWithSuggestions(true)
    }, [])

    const inputContextValue = useMemo<InputContext>(
      () => ({
        caretRef,
        inputFocused: isInputFocused,
        inputKeyDown,
        inputOnBlur,
        inputOnChange,
        inputOnFocus,
        inputRef,
        inputSelectionEnd,
        inputSelectionStart,
        showSpinner,
        suspendFocus,
        updateInputSelection,
        updateStyledInputBlockCount,
      }),
      [
        inputKeyDown,
        inputOnBlur,
        inputOnChange,
        inputOnFocus,
        inputSelectionEnd,
        inputSelectionStart,
        isInputFocused,
        showSpinner,
        suspendFocus,
        updateInputSelection,
        updateStyledInputBlockCount,
      ],
    )

    return <InputContext.Provider value={inputContextValue}>{children}</InputContext.Provider>
  },
)

InputContextProvider.displayName = 'InputContextProvider'

try{ InputContext.displayName ||= 'InputContext' } catch {}